import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import buttonStyles from '../YesNoButtonsCSS/YesNoButtons.module.css';
import DatePicker from '../DatePicker/DatePicker';
import CommentBox from '../CommentBox/CommentBox';
import Dropdown from '../DropDownBox/Dropdown';
import { useDispatch, useSelector } from '../../redux/store';
import { editLeaveRequest, clearMessages, cancelLeaveRequest } from '../../redux/slices/holiday';
import ConfirmationPopUp from '../ConfirmationPopUp/ConfirmationPopUp';
import { useMsal } from "@azure/msal-react";
import { useSnackbar } from 'notistack';
import { fetchUkPublicHolidays } from '../../redux/slices/publicHolidays';

// Make sure the app element is set for accessibility. Only needs to be set once in the application lifecycle, but
// doing it here keeps the component self-contained and safe if it is rendered before the root sets it.
if (typeof document !== 'undefined') {
  Modal.setAppElement('#root');
}

/**
 * EditLeavePopup – simple modal that allows an admin to edit an existing leave (holiday / sickness / study etc).
 *
 * Props:
 *  isOpen      – Boolean that toggles the modal visibility
 *  onClose     – Callback when the user closes the modal (cancel or after save)
 *  onSave      – Callback for backward compatibility (no longer used for data updates)
 *  leaveData   – The current leave row object to edit. Should contain at least { id, dates, type, length, used }
 *  onDataReload – Callback to trigger parent component data refresh
 */
function EditLeavePopup({ isOpen, onClose, onSave, leaveData = {}, onDataReload }) {
  const dispatch = useDispatch();
  const { accounts } = useMsal();
  const account = accounts[0];
  const { enqueueSnackbar } = useSnackbar();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const { isUpdating, showSuccess, showError, errorMessage } = useSelector(state => state.holiday);

  // Local state mirrors the editable fields
  const [startDateISO, setStartDateISO] = useState(null); // ISO strings
  const [endDateISO, setEndDateISO] = useState(null);
  const [type, setType] = useState('Holiday');
  const [length, setLength] = useState('');
  const [used, setUsed] = useState('');
  const [startTime, setStartTime] = useState('Morning');
  const [endTime, setEndTime] = useState('Afternoon');
  const [showPlannerNote, setShowPlannerNote] = useState(false);

  // State for bank holidays
  const [bankHolidays, setBankHolidays] = useState([]);

  // Clear Redux messages when component opens
  useEffect(() => {
    if (isOpen) {
      dispatch(clearMessages());
    }
  }, [isOpen, dispatch]);

  // Fetch bank holidays when component opens
  useEffect(() => {
    if (isOpen) {
      fetchBankHolidays();
    }
  }, [isOpen]);

  // Fetch UK bank holidays
  const fetchBankHolidays = async () => {
    try {
      // Get current year and next year to handle cross-year leaves
      const currentYear = new Date().getFullYear();
      const nextYear = currentYear + 1;

      const dates = await dispatch(fetchUkPublicHolidays({ years: [currentYear, nextYear] })).unwrap();
      setBankHolidays(Array.isArray(dates) ? dates : []);
    } catch (error) {
      console.error('Error fetching bank holidays:', error);
      setBankHolidays([]); // Fallback to empty array
      // Don't show error to user as this is supplementary functionality
    }
  };

  // Helper: weekend check
  const isWeekend = (dateObj) => {
    const day = dateObj.getDay();
    return day === 0 || day === 6; // Sunday=0, Saturday=6
  };

  // Helper: bank holiday check
  const isBankHoliday = (dateObj) => {
    const dateStr = dateObj.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    return bankHolidays.includes(dateStr);
  };

  // Helper: working day check (not weekend and not bank holiday)
  const isWorkingDay = (dateObj) => {
    return !isWeekend(dateObj) && !isBankHoliday(dateObj);
  };

  // Recalculate length whenever dates change (include bank holidays in dependencies)
  useEffect(() => {
    const computeLength = () => {
      if (!startDateISO || !endDateISO) return;

      const start = new Date(startDateISO);
      const end = new Date(endDateISO);
      if (isNaN(start) || isNaN(end) || end < start) return;

      // Apply half-day rules based on startTime / endTime
      let daysTaken = 0;
      const startDate = new Date(startDateISO);
      const endDate = new Date(endDateISO);

      let current = new Date(startDate);
      while (current <= endDate) {
        // Only count working days (exclude weekends and bank holidays)
        if (isWorkingDay(current)) {
          if (current.getTime() === startDate.getTime() && current.getTime() === endDate.getTime()) {
            // same day
            if (startTime === 'Morning' && endTime === 'Morning') daysTaken += 0.5;
            else if (startTime === 'Afternoon' && endTime === 'Afternoon') daysTaken += 0.5;
            else if (startTime === 'Afternoon' && (endTime === 'End of day' || endTime === 'PM')) daysTaken += 0.5;
            else if (startTime === 'Full Day' || endTime === 'Full Day') daysTaken += 1;
            else if (startTime === 'Morning' && endTime === 'Afternoon') daysTaken += 0.5;
            else if (startTime === 'Morning' && (endTime === 'End of day' || endTime === 'PM')) daysTaken += 1;
            else daysTaken += 1;
          } else if (current.getTime() === startDate.getTime()) {
            if (startTime === 'Afternoon') daysTaken += 0.5; else daysTaken += 1;
          } else if (current.getTime() === endDate.getTime()) {
            if (endTime === 'Morning') daysTaken += 0.5;
            else if (endTime === 'Full Day') daysTaken += 1;
            else daysTaken += 1;
          } else {
            daysTaken += 1;
          }
        }
        current.setDate(current.getDate() + 1);
      }

      setLength(String(daysTaken));
      setUsed(String(daysTaken));
    };

    computeLength();
  }, [startDateISO, endDateISO, startTime, endTime, bankHolidays]);

  // Populate initial state when leaveData changes
  useEffect(() => {
    if (!leaveData) {
      setShowPlannerNote(false);
      return;
    };

    // Check if the leave is in the past to show the planner note
    if (leaveData.endDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Normalize today to the start of the day

      const endDateParts = leaveData.endDate.split('-').map(Number);
      const endDate = new Date(endDateParts[0], endDateParts[1] - 1, endDateParts[2]);

      if (endDate < today) {
        setShowPlannerNote(true);
      } else {
        setShowPlannerNote(false);
      }
    } else {
      setShowPlannerNote(false);
    }

    // Priority: explicit ISO fields from backend
    if (leaveData.startDate) {
      const iso = leaveData.startDate.includes('T') ? leaveData.startDate : `${leaveData.startDate}T00:00:00Z`;
      setStartDateISO(new Date(iso).toISOString());
    }

    if (leaveData.endDate) {
      const iso = leaveData.endDate.includes('T') ? leaveData.endDate : `${leaveData.endDate}T00:00:00Z`;
      setEndDateISO(new Date(iso).toISOString());
    }

    // Try to parse the dates field if it exists in a "DD/MM/YYYY – DD/MM/YYYY" format.
    else if (leaveData.dates) {
      const parts = leaveData.dates.split(/-|–/); // dash or en-dash
      if (parts.length === 2) {
        const [startStr, endStr] = parts.map(p => p.trim());
        const parseDate = (str) => {
          const [day, month, year] = str.split('/');
          if (!day || !month || !year) return null;
          return new Date(`${year}-${month}-${day}`);
        };
        const startDate = parseDate(startStr);
        const endDate = parseDate(endStr);
        if (startDate && !isNaN(startDate)) {
          setStartDateISO(startDate.toISOString());
        }
        if (endDate && !isNaN(endDate)) {
          setEndDateISO(endDate.toISOString());
        }
      }
    }

    if (leaveData.type) setType(leaveData.type);
    if (leaveData.length) setLength(leaveData.length);
    if (leaveData.used) setUsed(leaveData.used);
    // Use explicit startPart/endPart from backend if available
    if (leaveData.startPart) {
      const normalizeTime = (part) => {
        const p = part.toLowerCase();
        if (p === 'am' || p === 'morning') return 'Morning';
        if (p === 'pm' || p === 'afternoon') return 'Afternoon';
        if (p === 'full day' || p === 'full' || p === 'all day') return 'Full Day';
        if (p === 'end of day' || p === 'eod') return 'End of day';
        return part; // fallback to original
      };
      setStartTime(normalizeTime(leaveData.startPart));
    }

    if (leaveData.endPart) {
      const normalizeTime = (part) => {
        const p = part.toLowerCase();
        if (p === 'am' || p === 'morning') return 'Morning';
        if (p === 'pm' || p === 'afternoon') return 'Afternoon';
        if (p === 'full day' || p === 'full' || p === 'all day') return 'Full Day';
        if (p === 'end of day' || p === 'eod') return 'End of day';
        return part; // fallback to original
      };
      setEndTime(normalizeTime(leaveData.endPart));
    }

    // Fallback: parse old partOfDay field if startPart/endPart not available
    if (!leaveData.startPart && !leaveData.endPart && leaveData.partOfDay) {
      const pod = leaveData.partOfDay.toLowerCase();
      if (pod === 'am' || pod === 'morning') {
        setStartTime('Morning');
        setEndTime('Morning');
      } else if (pod === 'pm' || pod === 'afternoon') {
        setStartTime('Afternoon');
        setEndTime('Afternoon');
      } else {
        setStartTime('Morning');
        setEndTime('Full Day');
      }
    }
  }, [leaveData]);

  // Handlers
  const handleDateChange = ({ startDate, endDate }) => {
    setStartDateISO(startDate);
    setEndDateISO(endDate);
  };

  // Handle save click with validation
  const handleSaveClick = () => {
    // Validation checks
    if (!startDateISO || !endDateISO) {
      enqueueSnackbar('Please select both start and end dates', { variant: 'error' });
      return;
    }

    // Check if end date is after start date
    const startDate = new Date(startDateISO);
    const endDate = new Date(endDateISO);
    
    if (endDate < startDate) {
      enqueueSnackbar('End date must be after or equal to start date', { variant: 'error' });
      return;
    }

    // Check if user has enough remaining holiday days (only for Holiday type)
    if (type === 'Holiday' && leaveData.remainingHolidays !== undefined) {
      const originalLength = parseFloat(leaveData.length) || 0;
      const newLength = parseFloat(length) || 0;
      const difference = newLength - originalLength;
      
      if (difference > 0 && difference > leaveData.remainingHolidays) {
        enqueueSnackbar(`Insufficient holiday balance. You have ${leaveData.remainingHolidays} days remaining`, { variant: 'error' });
        return;
      }
    }

    // Build updated object for Redux
    const updatedLeaveData = {
      ...leaveData,
      startDateISO,
      endDateISO,
      type,
      length,
      used,
      startTime,
      endTime,
    };
    
    // Dispatch Redux action
    dispatch(editLeaveRequest(updatedLeaveData, account.username));
  };

  // Handle cancel leave flow
  const handleCancelLeave = () => {
    if (!leaveData?.id) {
      enqueueSnackbar('Unable to cancel: missing leave ID', { variant: 'error' });
      return;
    }
    setShowCancelConfirm(true);
  };

  const confirmCancelLeave = () => {
    setShowCancelConfirm(false);
    dispatch(cancelLeaveRequest(leaveData.id, account.username));
  };

  const closeCancelConfirm = () => setShowCancelConfirm(false);
  
  // Handle Redux success state
  useEffect(() => {
    if (showSuccess) {
      // Show success toast only once
      enqueueSnackbar('Leave updated successfully!', { variant: 'success' });
      
      // Trigger parent data reload to refresh allowances
      if (onDataReload) {
        setTimeout(() => {
          onDataReload();
        }, 500);
      }
      
      // Close popup - data will be refreshed via API calls
      onClose();
    }
  }, [showSuccess]); // Only depend on showSuccess to prevent spam

  // Handle errors with toast notifications
  useEffect(() => {
    if (showError && errorMessage) {
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [showError, errorMessage, enqueueSnackbar]);

  const formatDatesForDisplay = (startISO, endISO) => {
    if (!startISO || !endISO) return leaveData.dates || '';
    const dateToStr = (iso) => {
      const d = new Date(iso);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    };
    return `${dateToStr(startISO)} – ${dateToStr(endISO)}`;
  };

  const customStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 11000,
    },
    content: {
      maxHeight: '90%',
      maxWidth: '90%',
      minHeight: '260px',
      minWidth: '350px',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#eaf2f9',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      padding: '20px 30px',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      border: '1px solid #ccc',
      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    },
  };

  return (
    <>
    <Modal isOpen={isOpen} onRequestClose={onClose} style={customStyles} contentLabel="Edit Leave Modal">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
        <h2 style={{ margin: 0, fontFamily: 'PT Sans', textAlign: 'center' }}>Edit Leave</h2>

        {/* Planner Reminder Note */}
        {showPlannerNote && (
          <div style={{
            marginTop: '15px',
            padding: '10px',
            backgroundColor: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: '4px',
            color: 'rgba(0, 0, 0, 0.85)',
            fontSize: '12px',
            textAlign: 'center'
          }}>
            <strong>Note:</strong> Remember to manually move, rename or re-create tasks in Planner to match these changes.
          </div>
        )}

        {/* Start Date */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '14px', marginBottom: '4px' }}>Start Date</label>
          <DatePicker
            initialDate={startDateISO ? new Date(startDateISO) : new Date()}
            onDatesChange={({ startDate }) => setStartDateISO(startDate)}
            visableIcon={true}
          />
        </div>

        {/* End Date */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '14px', marginBottom: '4px' }}>End Date</label>
          <DatePicker
            initialDate={endDateISO ? new Date(endDateISO) : new Date()}
            onDatesChange={({ startDate }) => setEndDateISO(startDate)}
            visableIcon={true}
          />
        </div>

        {/* Type */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '14px', marginBottom: '4px' }}>Type</label>
          <CommentBox
            initialData={type}
            onValueChange={setType}
            placeholder="e.g. Holiday, Sick Leave, Study"
          />
        </div>

        {/* Start Time */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '14px', marginBottom: '4px' }}>Start Time</label>
          <Dropdown
            options={['Morning', 'Afternoon', 'Full Day', 'End of day']}
            initialSelectedValue={startTime}
            onValueChange={setStartTime}
            PopUp={true}
          />
        </div>

        {/* End Time */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '14px', marginBottom: '4px' }}>End Time</label>
          <Dropdown
            options={['Morning', 'Afternoon', 'Full Day', 'End of day']}
            initialSelectedValue={endTime}
            onValueChange={setEndTime}
            PopUp={true}
          />
        </div>

        {/* Length (read-only) */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '14px', marginBottom: '4px' }}>Length</label>
          <CommentBox
            initialData={length}
            onValueChange={() => {}}
            locked={true}
          />
        </div>

        {/* Used (read-only) */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '14px', marginBottom: '4px' }}>Used</label>
          <CommentBox
            initialData={used}
            onValueChange={() => {}}
            locked={true}
          />
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '18px', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '140px', height: '32px' }}>
            <button 
              className={buttonStyles.YesButtonComponentStyling} 
              style={{
                width: '100%',
                opacity: isUpdating ? 0.6 : 1,
                cursor: isUpdating ? 'not-allowed' : 'pointer'
              }}
              onClick={handleSaveClick}
              disabled={isUpdating}
            >
              {isUpdating ? 'Saving…' : 'Save changes'}
            </button>
          </div>
          <div style={{ minWidth: '170px', height: '32px' }}>
            <button 
              className={buttonStyles.NoButtonComponentStyling} 
              style={{ width: '100%', borderColor: '#d32f2f', color: '#d32f2f' }}
              onClick={handleCancelLeave}
              disabled={isUpdating}
              title="Cancel this leave booking"
            >
              Cancel this booking
            </button>
          </div>
          <div style={{ minWidth: '110px', height: '32px' }}>
            <button className={buttonStyles.NoButtonComponentStyling} onClick={onClose} style={{ width: '100%' }}>
              Close
            </button>
          </div>
        </div>
        
        {/* Loading state indicator */}
        {isUpdating && (
          <div style={{ 
            marginTop: '10px', 
            padding: '8px', 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #2196f3', 
            borderRadius: '4px', 
            color: '#1976d2',
            fontSize: '12px',
            textAlign: 'center'
          }}>
            Updating leave request...
          </div>
        )}
      </div>
    </Modal>
    {/* Confirmation popup rendered outside the Modal to avoid overlay stacking issues */}
    <ConfirmationPopUp
      isVisible={showCancelConfirm}
      onClose={closeCancelConfirm}
      onConfirm={confirmCancelLeave}
      message={'Are you sure you want to cancel this leave booking? This will remove the booking and update timesheets automatically. Reminder: please update Planner to reflect this change.'}
    />
    </>
  );
}

export default EditLeavePopup; 